import { useEffect } from 'react';
import { useMoneyFormat, usePrefsValue } from '@adiox/ui';
import { useSymbols } from '../market/useSymbols';
import { useStream } from '../stream/stream';
import { useActiveLogin } from '../trading/useTrading';
import { useWorkspace } from '../workspace/workspaceStore';

export const BASE_TITLE = 'Adiox Terminal';
export type TabTitle = 'symbol' | 'profit' | 'off';

/**
 * The browser tab title: the active symbol and its bid ("EURUSD 1.08512 · Adiox"), the floating profit
 * ("+12.30 USD · Adiox"), or just the name. Falls back to the name while the figure is not known.
 */
export function documentTitle(
  mode: TabTitle,
  quote: { symbol: string; bid: number; digits: number } | null,
  floating: { value: number; currency: string; format: (v: number) => string } | null,
): string {
  if (mode === 'symbol' && quote) return `${quote.symbol} ${quote.bid.toFixed(quote.digits)} · Adiox`;
  if (mode === 'profit' && floating) {
    const sign = floating.value > 0 ? '+' : floating.value < 0 ? '−' : '';
    return `${sign}${floating.format(Math.abs(floating.value))} ${floating.currency} · Adiox`;
  }
  return BASE_TITLE;
}

/** Keeps the browser tab title up to date with what the user chose in Settings (the symbol and price by default). */
export function useDocumentTitle(): void {
  const raw = usePrefsValue((s) => s.data.tabTitle);
  const mode: TabTitle = raw === 'profit' || raw === 'off' ? raw : 'symbol';
  const symbol = useWorkspace((s) => s.activeSymbol);
  const bid = useStream((s) => (symbol ? s.quotes[symbol]?.bid : undefined));
  const digits = useSymbols().data?.find((s) => s.name === symbol)?.digits ?? 5;
  const login = useActiveLogin();
  const account = useStream((s) => (login === undefined ? undefined : s.accounts[login]));
  const format = useMoneyFormat();

  const title = documentTitle(
    mode,
    symbol && bid !== undefined ? { symbol, bid, digits } : null,
    account?.loaded ? { value: account.figures.floating, currency: account.currency, format } : null,
  );
  useEffect(() => {
    document.title = title;
  }, [title]);
  useEffect(() => () => void (document.title = BASE_TITLE), []);
}
